import React from 'react';
import { useAction } from 'wasp/client/operations';
import { 
  toggleLocationSubscription, 
  toggleOrganizationSubscription 
} from 'wasp/client/operations';
import { Button } from '../ui/button';
import { Bell, BellOff, Users, UserMinus } from 'lucide-react';
import { useAuth } from 'wasp/client/auth';
import { toast } from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';

interface SubscriptionButtonProps {
  type: 'LOCATION' | 'ORGANIZATION';
  id: string;
  isSubscribed: boolean;
  /** Location slug — used to navigate after subscribing */
  locationSlug?: string;
  /** When true, navigates to the location hub after a successful subscribe */
  redirectOnSubscribe?: boolean;
  onSuccess?: () => void;
  isIconOnly?: boolean;
  className?: string;
}

export const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({ 
  type, 
  id, 
  isSubscribed,
  locationSlug,
  redirectOnSubscribe = true,
  onSuccess,
  isIconOnly = false,
  className
}) => {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const toggleLocationAction = useAction(toggleLocationSubscription);
  const toggleOrgAction = useAction(toggleOrganizationSubscription);

  const [isLoading, setIsLoading] = React.useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    setIsLoading(true);
    try {
      if (type === 'LOCATION') {
        await toggleLocationAction({ locationId: id, isSubscribed: !isSubscribed });
      } else {
        await toggleOrgAction({ organizationId: id, isSubscribed: !isSubscribed });
      }
      
      const actionLabel = type === 'LOCATION' ? 'Subscription' : 'Follow';
      if (type === 'LOCATION' && !isSubscribed) {
        toast.success(
          (t) => (
            <span>
              Subscribed — notifications default to digest.{' '}
              <button
                type="button"
                className="underline font-medium"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/account?tab=notifications');
                }}
              >
                Customize
              </button>
            </span>
          ),
          { duration: 6000 }
        );
      } else {
        toast.success(isSubscribed ? `${actionLabel} removed` : `${actionLabel} successful`);
      }

      if (type === 'LOCATION' && !isSubscribed && redirectOnSubscribe && locationSlug) {
        navigate(`/${locationSlug}/information-desk`);
      }

      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to update subscription');
    } finally {
      setIsLoading(false);
    }
  };

  if (isIconOnly) {
    return (
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={cn(
          "p-2 rounded-lg transition-colors flex items-center justify-center",
          isSubscribed ? "text-primary bg-primary/10" : "text-header-text-muted hover:bg-sidebar-hover",
          className
        )}
        title={isSubscribed ? (type === 'LOCATION' ? 'Unsubscribe' : 'Unfollow') : (type === 'LOCATION' ? 'Subscribe' : 'Follow')}
      >
        {isSubscribed ? 
          (type === 'LOCATION' ? <BellOff className="h-5 w-5" /> : <UserMinus className="h-5 w-5" />) : 
          (type === 'LOCATION' ? <Bell className="h-5 w-5" /> : <Users className="h-5 w-5" />)
        }
      </button>
    );
  }

  const label = type === 'LOCATION' 
    ? (isSubscribed ? 'Unsubscribe' : 'Subscribe') 
    : (isSubscribed ? 'Unfollow' : 'Follow');

  return (
    <Button
      variant={isSubscribed ? "outline" : "default"}
      size="sm"
      onClick={handleToggle}
      disabled={isLoading}
      className={cn("flex items-center gap-2", className)}
    >
      {isSubscribed ? 
        (type === 'LOCATION' ? <BellOff className="h-4 w-4" /> : <UserMinus className="h-4 w-4" />) : 
        (type === 'LOCATION' ? <Bell className="h-4 w-4" /> : <Users className="h-4 w-4" />)
      }
      {label}
    </Button>
  );
};
